import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";

import {
  endingRefusalOf,
  type Assessment,
  type AssessmentId,
  type GuestOnCase,
  type GuestOnCaseDetails,
  type PersonInvolvement,
  type PersonInvolvementId,
  type PersonState,
} from "@thalamos/haven-common";

import {
  addingTo,
  AddInvolvementsDialog,
  type AddingTo,
} from "../../components/AddInvolvementsDialog/AddInvolvementsDialog.js";
import {
  CaseInvolvement,
  type CaseInvolvementActor,
  type CaseInvolvementManagement,
} from "../../components/CaseInvolvement/CaseInvolvement.js";
import {
  EndGuestAccessDialog,
  type GuestAccessEndingOutcome,
  type GuestAccessToEnd,
} from "../../components/EndGuestAccessDialog/EndGuestAccessDialog.js";
import {
  GuestDetailsDialog,
  type GuestDetailsReading,
} from "../../components/GuestDetailsDialog/GuestDetailsDialog.js";
import {
  EndInvolvementDialog,
  ReplaceResponsibleUserDialog,
  type PartySearch,
} from "../../components/InvolvementDialogs/InvolvementDialogs.js";
import { useToast } from "../../components/ToastProvider/ToastProvider.js";
import type { InvolvementManagement } from "./involvementManagement.js";

/**
 * The guests on the case, and what this reader may do with them. Details is
 * offered where `readDetails` is given and `mayOpenDetails` holds of the
 * guest; End where `onEnd` is given.
 */
export type CaseGuestsSection = {
  /** Undefined until read, which is not the same as none. */
  guests: GuestOnCase[] | undefined;
  failed: boolean;
  mayOpenDetails: (guest: GuestOnCase) => boolean;
  readDetails?: (
    involvementId: PersonInvolvementId,
  ) => Promise<GuestOnCaseDetails | undefined>;
  onEnd?: (
    involvementId: PersonInvolvementId,
  ) => Promise<GuestAccessEndingOutcome>;
  assessmentHref: (assessmentId: AssessmentId) => string;
};

/** What End's confirmation says the guest loses. */
const accessToEnd = (guest: GuestOnCase): GuestAccessToEnd =>
  match(guest.access)
    .returnType<GuestAccessToEnd>()
    .with({ kind: "read_shared_assessments" }, ({ assessmentCount }) => ({
      kind: "read_shared_assessments",
      guestName: guest.name,
      sharedCount: assessmentCount,
    }))
    .with({ kind: "complete_assessment" }, () => ({
      kind: "complete_assessment",
      guestName: guest.name,
    }))
    .exhaustive();

/**
 * The profile's Case involvement tab: every role, who holds it, and the
 * guests on the case.
 *
 * TODO(case-involvement): offer Replace on the other one-holder roles.
 */
export const CaseInvolvementTab = ({
  personName,
  state,
  involvements,
  involvementsFailed,
  assessments,
  actor,
  management,
  onAllocateResponsibleUser,
  guests,
}: {
  personName: string;
  state: PersonState | undefined;
  involvements: PersonInvolvement[] | undefined;
  involvementsFailed: boolean;
  /**
   * The young person's assessments, for what they hold back from ending. Until
   * they are read nothing is held back here; the server still refuses such an
   * ending.
   */
  assessments: readonly Assessment[] | undefined;
  /** Absent for a reader, who is offered no changes. */
  actor: CaseInvolvementActor | undefined;
  /** Absent for a reader who may not change who is involved. */
  management:
    | (PartySearch &
        Pick<
          InvolvementManagement,
          "onAdd" | "onEnd" | "onReplaceResponsibleUser" | "onRefresh"
        >)
    | undefined;
  /** Absent unless this reader may name who runs a case nobody runs. */
  onAllocateResponsibleUser: (() => void) | undefined;
  /** Absent where the page lists no guests. */
  guests?: CaseGuestsSection | undefined;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [adding, setAdding] = useState<AddingTo | null>(null);
  const [detailsOf, setDetailsOf] = useState<GuestOnCase | null>(null);
  const [reading, setReading] = useState<GuestDetailsReading>({
    kind: "reading",
  });
  const [endingGuest, setEndingGuest] = useState<GuestOnCase | null>(null);
  const detailsWanted = useRef<PersonInvolvementId | null>(null);

  const closeDetails = () => {
    detailsWanted.current = null;
    setDetailsOf(null);
  };

  const readDetails = guests?.readDetails;
  const onEndGuest = guests?.onEnd;

  const openDetails = async (guest: GuestOnCase) => {
    // Unreachable: Details is offered only where it can be read.
    /* v8 ignore if */
    if (readDetails === undefined) {
      return;
    }

    detailsWanted.current = guest.involvementId;
    setDetailsOf(guest);
    setReading({ kind: "reading" });
    const details = await readDetails(guest.involvementId).catch(
      () => undefined,
    );

    // A read that lands after its dialog closed, or after another guest's was
    // opened, would show one guest's address under another's name.
    if (detailsWanted.current !== guest.involvementId) {
      return;
    }

    setReading(
      details === undefined ? { kind: "failed" } : { kind: "read", details },
    );
  };
  const [ending, setEnding] = useState<PersonInvolvement | null>(null);
  const [replacing, setReplacing] = useState<PersonInvolvement | null>(null);

  const changes: CaseInvolvementManagement | undefined =
    actor === undefined
      ? undefined
      : {
          actor,
          onAdd:
            onAllocateResponsibleUser === undefined && management === undefined
              ? undefined
              : (kind, partyKind) => {
                  // Who runs the case is named rather than added alongside
                  // somebody, so it has a dialog of its own.
                  if (kind === "responsible_user") {
                    onAllocateResponsibleUser?.();
                    return;
                  }

                  setAdding(addingTo(kind, partyKind) ?? null);
                },
          onReplace:
            management === undefined
              ? undefined
              : (involvement) => {
                  setReplacing(involvement);
                },
          replaces: ["responsible_user"],
          onEnd:
            management === undefined
              ? undefined
              : (involvement) => {
                  setEnding(involvement);
                },
          endingRefusal: (involvement) =>
            endingRefusalOf(involvement.kind, assessments ?? []),
        };

  return (
    <>
      <CaseInvolvement
        involvements={involvements}
        failed={involvementsFailed}
        missing={state?.kind === "unassigned" ? state.missing : []}
        management={changes}
        guests={
          guests === undefined
            ? undefined
            : {
                guests: guests.guests,
                failed: guests.failed,
                actions: {
                  mayOpenDetails: guests.mayOpenDetails,
                  onOpenDetails:
                    readDetails === undefined ? undefined : openDetails,
                  onEnd:
                    onEndGuest === undefined
                      ? undefined
                      : (guest) => {
                          setEndingGuest(guest);
                        },
                },
              }
        }
      />

      {guests === undefined ? null : (
        <GuestDetailsDialog
          guest={detailsOf}
          reading={reading}
          assessmentHref={guests.assessmentHref}
          onEnd={
            onEndGuest === undefined || detailsOf === null
              ? undefined
              : () => {
                  setEndingGuest(detailsOf);
                }
          }
          onClose={closeDetails}
        />
      )}

      {onEndGuest === undefined ? null : (
        <EndGuestAccessDialog
          guest={endingGuest === null ? null : accessToEnd(endingGuest)}
          onClose={() => {
            setEndingGuest(null);
          }}
          onEnd={async () => {
            // The dialog is open only while a guest is chosen.
            /* v8 ignore if */
            if (endingGuest === null) {
              throw new Error("End was confirmed with no guest chosen");
            }

            const outcome = await onEndGuest(endingGuest.involvementId);

            if (outcome === "ended") {
              closeDetails();
              toast.show(
                t(`endGuestAccess.ended.${endingGuest.access.kind}`, {
                  name: endingGuest.name,
                }),
              );
            }

            return outcome;
          }}
        />
      )}

      {management === undefined ? null : (
        <AddInvolvementsDialog
          adding={adding}
          personName={personName}
          involvements={involvements ?? []}
          search={management}
          onAdd={management.onAdd}
          onRefresh={management.onRefresh}
          onClose={() => {
            setAdding(null);
          }}
        />
      )}

      {ending === null || management === undefined ? null : (
        <EndInvolvementDialog
          key={ending.id}
          involvement={ending}
          state={state}
          onClose={() => {
            setEnding(null);
          }}
          onEnd={management.onEnd}
          onRefresh={management.onRefresh}
        />
      )}

      {replacing?.party.kind !== "user" || management === undefined ? null : (
        <ReplaceResponsibleUserDialog
          key={replacing.id}
          open
          search={management}
          outgoing={{
            involvementId: replacing.id,
            userId: replacing.party.userId,
            organisationId: replacing.party.onBehalfOfOrganisationId,
            name: replacing.party.name,
          }}
          onReplaceResponsibleUser={management.onReplaceResponsibleUser}
          onRefresh={management.onRefresh}
          onClose={() => {
            setReplacing(null);
          }}
        />
      )}
    </>
  );
};
